import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { IndustrialBlueprintStyles } from "./IndustrialBlueprintStyles";
import { LaunchpadBoldRuntime } from "./LaunchpadBoldRuntime";

function LaunchpadBoldContrastStyles() {
    return (
        <style>{`
            [data-web-template="launchpad-bold"]
            [data-web-region="header"][data-industrial-mobile-menu-open]
            .site-navigation-marker-industrial {
                color: var(--site-accent) !important;
            }
        `}</style>
    );
}

function LaunchpadBoldFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main className="px-5 py-20 sm:px-6" data-builder-document-region="page">
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-accent)] [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-extrabold [&_h1]:uppercase [&_h1]:leading-[.84] [&_h1]:tracking-[-.055em] [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-extrabold [&_h2]:uppercase [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const launchpadBoldTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "launchpad-bold",
        name: "Startup a SaaS produkt",
        type: "landing",
        category: "landing",
        description: "Výrazná konverzní šablona pro SaaS, nový digitální produkt, startup nebo časově omezenou kampaň.",
        previewImage: "/templates/launchpad-bold/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["launchpad-bold"],
        pageTypes: {
            home: { label: "Bold landing", slots: ["richContent"] },
            content: { label: "Product detail", slots: ["richContent"] },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            launchpadBoldTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                className="web-industrial-blueprint min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                data-industrial-template="launchpad-bold"
                data-web-template="launchpad-bold"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <IndustrialBlueprintStyles />
                <LaunchpadBoldContrastStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <LaunchpadBoldRuntime
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <LaunchpadBoldFallbackPage
                            design={design}
                            LinkComponent={LinkComponent}
                            props={props}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
